import {
  ContextualData,
  JourneyEventContextualData,
  emptyContextualData,
  emptyJourneyEventContextualData,
  mergeContextualData,
} from './contextualData';
import { Observability, TrackingOptions } from './declarations';
import {
  DatadogRumClientInstance,
  EventTrackingAction,
  EventTrackingCategory,
  JourneyClientInstance,
  SentryClientInstance,
  UserEvent,
} from './declarations';
import { globalObservability } from './global';
import {
  addRUMGlobalContext,
  getContextualDataFromDatadog,
  trackErrorInDatadog,
  trackEventInDatadog,
  trackPerformanceInDatadog,
  trackUserEventInDatadog,
  trackJourneyEventInDatadog,
} from './internal/datadog';
import { trackEvent as analyticsTrackEvent } from './internal/analytics';
import { trackUserEventInJourney, trackJourneyEventInJourney } from './internal/journey';
import {
  trackErrorInSentry,
  trackEventInSentry,
  trackUserEventInSentry,
  trackJourneyEventInSentry,
} from './internal/sentry';
import { BaseJourneyEventWrapper, JourneyEventHelpers } from './declarations/JourneyEvent';

/** The real thing. It actually calls services like Google Analytics, Segment, Sentry, etc. */
export class RealObservability<
  AppCategory,
  AppActions,
  AppJourneyEventWrapper extends BaseJourneyEventWrapper,
> implements Observability<AppCategory, AppActions, AppJourneyEventWrapper>
{
  sentryClient: SentryClientInstance = {};
  datadogRumClient: DatadogRumClientInstance = {};
  journeyUserClient: JourneyClientInstance = {};
  journeyEventClient: JourneyClientInstance = {};

  constructor(
    readonly eventTrackingCategory: EventTrackingCategory<AppCategory>,
    readonly journeyEventHelpers?: JourneyEventHelpers,
  ) {}

  setSentryClient(sentryClient: SentryClientInstance): void {
    this.sentryClient = sentryClient;
  }

  getSentryClient(): SentryClientInstance {
    return this.sentryClient;
  }

  setJourneyUserClient(journeyClient: JourneyClientInstance): void {
    this.journeyUserClient = journeyClient;
  }

  setJourneyEventClient(journeyClient: JourneyClientInstance): void {
    this.journeyEventClient = journeyClient;
  }

  getJourneyUserClient(): JourneyClientInstance {
    return this.journeyUserClient;
  }

  getJourneyEventClient(): JourneyClientInstance {
    return this.journeyEventClient;
  }

  setDatadogRumClient(datadogRumClient: DatadogRumClientInstance): void {
    this.datadogRumClient = datadogRumClient;
  }

  getDatadogRumClient(): DatadogRumClientInstance {
    return this.datadogRumClient;
  }

  trackEvent(
    action: EventTrackingAction<AppActions>,
    label?: string,
    contextualData: ContextualData = emptyContextualData,
    options?: TrackingOptions<AppCategory>,
  ): void {
    // Add contextual data from Datadog (e.g. datadog-rum-session-id).
    contextualData = mergeContextualData(contextualData, getContextualDataFromDatadog());

    analyticsTrackEvent<AppCategory, AppActions>(
      options?.categoryOverride ?? this.eventTrackingCategory,
      action,
      label,
      contextualData.data,
    );
    trackEventInDatadog<AppActions>(action, label, contextualData);
    trackEventInSentry<AppActions>(action, label, contextualData);

    if (options?.addTiming) {
      trackPerformanceInDatadog(`${this.eventTrackingCategory}-${action}-${label}`);
    }
  }

  trackUserEvent(event: UserEvent, contextualData: ContextualData = emptyContextualData): void {
    // TODO (billy)
    // do we need to add contextual data from datadog?
    trackUserEventInJourney(event, contextualData);
    trackUserEventInDatadog(event, contextualData);
    trackUserEventInSentry(event, contextualData);
  }

  trackJourneyEvent(
    event: Pick<AppJourneyEventWrapper, 'category'>,
    contextualData: JourneyEventContextualData = emptyJourneyEventContextualData,
  ): void {
    if (!this.journeyEventHelpers)
      throw new Error(
        'Provide journeyEventHelpers and EventWrapper types in initObservability to use trackJourneyEvent',
      );
    trackJourneyEventInJourney<AppJourneyEventWrapper>(
      event,
      contextualData,
      this.journeyEventHelpers,
    );
    trackJourneyEventInDatadog<AppJourneyEventWrapper>(
      event,
      contextualData,
      this.journeyEventHelpers,
    );
    trackJourneyEventInSentry<AppJourneyEventWrapper>(
      event,
      contextualData,
      this.journeyEventHelpers,
    );
  }

  trackError(
    label: string,
    error?: Error,
    contextualData: ContextualData = emptyContextualData,
    options?: TrackingOptions<AppCategory>,
  ): void {
    // Add contextual data from Datadog (e.g. datadog-rum-session-id).
    contextualData = mergeContextualData(contextualData, getContextualDataFromDatadog());

    // Add contextual data from Sentry (e.g. sentry-event-id).
    const sentryContextualData = trackErrorInSentry(label, error, contextualData);
    contextualData = mergeContextualData(contextualData, sentryContextualData);

    trackErrorInDatadog(label, error, contextualData);
    analyticsTrackEvent(this.eventTrackingCategory, 'error', label, contextualData.data);

    if (options?.addTiming) {
      trackPerformanceInDatadog(`${this.eventTrackingCategory}-error-${label}`);
    }
  }
}

export const testonly = {
  initDebugObservability(opts: DebugObservabilityProps = {}) {
    globalObservability.setCurrentInstance(new DebugObservability({ log: true, ...opts }));
  },
};

export type DebugObservabilityProps =
  | {
      sentryClient?: SentryClientInstance;
      journeyUserClient?: JourneyClientInstance;
      journeyEventClient?: JourneyClientInstance;
      datadogRumClient?: DatadogRumClientInstance;
      log?: boolean;
    }
  | undefined;

/** For debugging only. Logs events and errors to console. */
export class DebugObservability<
  AppCategory,
  AppActions,
  AppJourneyEventWrapper extends BaseJourneyEventWrapper,
> implements Observability<AppCategory, AppActions, AppJourneyEventWrapper>
{
  sentryClient: SentryClientInstance = {};
  journeyUserClient: JourneyClientInstance = {};
  journeyEventClient: JourneyClientInstance = {};
  datadogRumClient: DatadogRumClientInstance = {};
  log: boolean = false;
  constructor(opts: DebugObservabilityProps = {}) {
    this.sentryClient = opts?.sentryClient || {};
    this.journeyUserClient = opts?.journeyUserClient || {};
    this.journeyEventClient = opts?.journeyEventClient || {};
    this.datadogRumClient = opts?.datadogRumClient || {};
    this.log = opts?.log || false;
  }

  trackEvent(
    action: EventTrackingAction<AppActions>,
    label?: string,
    contextualData: ContextualData = emptyContextualData,
  ): void {
    this.log && console.info(`trackEvent(${action}, ${label})`, contextualData);
  }

  trackUserEvent(event: UserEvent, contextualData: ContextualData = emptyContextualData): void {
    this.log && console.info(`trackUserEvent(${JSON.stringify(event)})`, contextualData);
  }

  trackJourneyEvent(
    event: AppJourneyEventWrapper,
    contextualData: JourneyEventContextualData = emptyJourneyEventContextualData,
  ): void {
    this.log && console.info(`trackJourneyEvent(${JSON.stringify(event)})`, contextualData);
  }

  trackError(
    label: string,
    error?: Error,
    contextualData: ContextualData = emptyContextualData,
  ): void {
    this.log && console.error(`trackError(${label})`, error, contextualData);
  }
  setDatadogRumClient(datadogRumClient: DatadogRumClientInstance): void {
    this.datadogRumClient = datadogRumClient;
  }
  getDatadogRumClient(): DatadogRumClientInstance {
    return this.datadogRumClient;
  }
  setJourneyUserClient(journeyClient: JourneyClientInstance): void {
    this.journeyUserClient = journeyClient;
  }
  setJourneyEventClient(journeyClient: JourneyClientInstance): void {
    this.journeyEventClient = journeyClient;
  }
  getJourneyUserClient(): JourneyClientInstance {
    return this.journeyUserClient;
  }
  getJourneyEventClient(): JourneyClientInstance {
    return this.journeyEventClient;
  }
  setSentClient(sentryClient: SentryClientInstance): void {
    this.sentryClient = sentryClient;
  }
  getSentryClient(): SentryClientInstance {
    return this.sentryClient;
  }
}

/**
 * This will explicitly add contextual data to the global context for Datadog RUM.
 * It is not intended to include everything passed into trackEvent's contextData
 * which is surfaced in custom RUM events and segment. This should be used for
 * things like experimentation treatments, etc.
 */
export const addDatadogRUMGlobalContext = (context: { [k: string]: unknown }) => {
  addRUMGlobalContext(context);
};
